import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import type { User } from "@/hooks/use-users";
import { useSetRoles } from "@/hooks/use-users";

const ALL_ROLES: { value: string; label: string }[] = [
  { value: "admin", label: "Admin" },
  { value: "compliance_manager", label: "Compliance Manager" },
  { value: "compliance_auditor", label: "Compliance Auditor" },
  { value: "tech_partner", label: "Tech Partner" },
  { value: "stakeholder", label: "Stakeholder" },
];

interface ChangeRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
}

export function ChangeRoleDialog({ open, onOpenChange, user }: ChangeRoleDialogProps) {
  const currentRoles = user.roles?.length ? user.roles : [user.role];
  const [selected, setSelected] = useState<string[]>(currentRoles);
  const setRoles = useSetRoles();

  function toggle(value: string) {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value],
    );
  }

  function handleSubmit() {
    if (selected.length === 0) return;
    const sorted = ALL_ROLES.map((r) => r.value).filter((v) => selected.includes(v));
    setRoles.mutate({ userId: user.id, roles: sorted }, { onSuccess: () => onOpenChange(false) });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change Roles</DialogTitle>
          <DialogDescription>Update the roles for {user.name}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {ALL_ROLES.map(({ value, label }) => (
            <div key={value} className="flex items-center gap-3">
              <Checkbox
                id={`role-${value}`}
                checked={selected.includes(value)}
                onCheckedChange={() => toggle(value)}
              />
              <Label htmlFor={`role-${value}`} className="cursor-pointer font-normal">
                {label}
              </Label>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={setRoles.isPending || selected.length === 0}>
            {setRoles.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
